import { ComponentType } from 'react';
import App from 'next/app';
import { Montserrat } from 'next/font/google';
import Head from 'next/head';
import Router from 'next/router';
import Script from 'next/script';
import { Loader } from '@googlemaps/js-api-loader';
import { CSSProp, createGlobalStyle, ThemeProvider } from 'styled-components';

import { FB_DOMAIN_VERIFICATION_ID } from '../libs/fpixel';
import * as fbq from '../libs/fpixel';
import { GA_TRACKING_ID } from '../libs/gtag';
import * as gtag from '../libs/gtag';
import * as gtm from '../libs/gtm';
import I18n from '../libs/i18n';
import defaultTheme from '../common/themes/default';
import cookiesData from '../public/lib/cookiesData';

require('antd/dist/antd.less');

declare module 'react' {
  interface HTMLAttributes<T> extends DOMAttributes<T> {
    css?: CSSProp;
  }
}

declare global {
  interface Window {
    initCookieConsent: any;
  }
}

const modalHeaderHeight = '55px';
const modalFooterHeight = '65px';

const GlobalStyle = createGlobalStyle`
  /* http://meyerweb.com/eric/tools/css/reset/ 
    v2.0 | 20110126
    License: none (public domain)
  */

  html, body, div, span, applet, object, iframe,
  h1, h2, h3, h4, h5, h6, p, blockquote, pre,
  a, abbr, acronym, address, big, cite, code,
  del, dfn, em, img, ins, kbd, q, s, samp,
  small, strike, strong, sub, sup, tt, var,
  b, u, i, center,
  dl, dt, dd, ol, ul, li,
  fieldset, form, label, legend,
  table, caption, tbody, tfoot, thead, tr, th, td,
  article, aside, canvas, details, embed, 
  figure, figcaption, footer, header, hgroup, 
  menu, nav, output, ruby, section, summary,
  time, mark, audio, video {
    margin: 0;
    padding: 0;
    border: 0;
    font-size: 100%;
    font: inherit;
    vertical-align: baseline;
  }
  /* HTML5 display-role reset for older browsers */
  article, aside, details, figcaption, figure, 
  footer, header, hgroup, menu, nav, section {
    display: block;
  }
  body {
    line-height: 1;
  }
  ol, ul {
    list-style: none;
  }
  blockquote, q {
    quotes: none;
  }
  blockquote:before, blockquote:after,
  q:before, q:after {
    content: '';
    content: none;
  }
  table {
    border-collapse: collapse;
    border-spacing: 0;
  }
  strong {
    font-weight: 600;
  }

  :root {
    @media ${(props) => props.theme.breakpoints.xs} {
      --gutter: ${(props) => props.theme.grid.xsGutter};
    }
    @media ${(props) => props.theme.breakpoints.sm} {
      --gutter: ${(props) => props.theme.grid.smGutter};
    }
    @media ${(props) => props.theme.breakpoints.md} {
      --gutter: ${(props) => props.theme.grid.mdGutter};
    }
    @media ${(props) => props.theme.breakpoints.lg} {
      --gutter: ${(props) => props.theme.grid.lgGutter};
    }
    @media ${(props) => props.theme.breakpoints.xl} {
      --gutter: ${(props) => props.theme.grid.xlGutter};
    }
    @media ${(props) => props.theme.breakpoints.xxl} {
      --gutter: ${(props) => props.theme.grid.xxlGutter};
    }
  }

  .no-opacity-dropdown {
    &.ant-dropdown-hidden, .ant-dropdown-menu-hidden, .ant-dropdown-menu-submenu-hidden {
      display: blocK;
      opacity: 0;
      pointer-events: none;
    }
  }

  .rtl-dropdown {
    .ant-dropdown-menu-item-group-title {
      font-size: 14px;
      text-align: left;
    }

    .ant-dropdown-menu-submenu-title {
      padding-right: 5px;
      padding-left: 24px;
      text-align: left;
    }

    .ant-dropdown-menu-submenu-title .ant-dropdown-menu-submenu-expand-icon {
      right: unset;
      left: 8px;
      margin-top: 1px;
      transform: rotate(180deg);
    }

    .ant-dropdown-menu-title-content {
      text-align: left;
    }
  }

  .breadcrumb-dropdown {
    .ant-dropdown-menu {
      max-height: 320px;
      overflow-y: auto;

      @media ${(props) => props.theme.breakpoints.xs} {
        max-height: 40vh;
      }
    }

  }

  .text-primary {
    color: ${(props) => props.theme.colors.primary} !important;
  }

  .font-weight-bold {
    font-weight: 600 !important;
  }

  .ant-btn-ghost.semi-ghost {
    background-color: rgba(255, 255, 255, 0.3);

    &:hover, &:focus, &:target {
      background-color: rgba(255, 255, 255, 0.4);
    }
  }

  .ant-dropdown-menu,
  .ant-select-dropdown:not(.search-dropdown) {
    border-radius: 0;
  }

  .ant-message,
  .ant-dropdown-menu,
  .ant-select-dropdown,
  .ant-modal,
  .ant-tooltip,
  .ant-popover {
    font-family: __Montserrat_237071;
    font-style: ${(props) => props.theme.font.style};
  }

  .ant-slider-tooltip {
    .ant.tooltip-inner {
      background-color: ${(props) => props.theme.colors.secondary};
      font-size: 12px;
    }
  }

  .ant-checkbox-inner {
    border-radius: 4px;
  }

  .ant-modal-mask, .ant-modal-wrap {
    z-index: 999999999;
  }

  .ant-modal {
    .ant-modal-header {
      padding-bottom: 16px;
      padding-top: 16px;
      height: ${modalHeaderHeight};
    }

    .ant-modal-footer {
      height: ${modalFooterHeight};
    }

    @media ${(props) => props.theme.breakpoints.xs} {
      width: 100vw !important;
      max-width: 100vw;
      margin: 0;
      padding: 0;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;

      .ant-modal-content {
        border-radius: 0;
      }

      .ant-modal-body {
        height: calc(100vh - ${modalHeaderHeight} - ${modalFooterHeight});
        height: calc(var(--vh-resizable, 1vh) * 100 - ${modalHeaderHeight} - ${modalFooterHeight});
        overflow-y: scroll;
      }
    }
  }

  .tippy-box[data-theme~='dark'] {
    background-color: #332e31;
    color: white;
  }

  .tippy-box {
    font-family: __Montserrat_237071;
    font-style: normal;
  }

  .sf-box div {
    font-family: __Montserrat_237071 !important;
  }
`;

const fbPixelIdInit = fbq.FB_PIXEL_ID
  ? `fbq('init', '${fbq.FB_PIXEL_ID}');`
  : '';
const fbSantaPolaPixelIdInit = fbq.FB_SANTA_POLA_PIXEL_ID
  ? `fbq('init', '${fbq.FB_SANTA_POLA_PIXEL_ID}');`
  : '';

const montserrat = Montserrat({
  subsets: ['latin'],
  weight: ['100', '200', '300', '400', '500', '600', '700', '800', '900'],
  display: 'swap',
});

class MyApp extends App<
  {},
  {},
  { previousUrl: string; cookieconsentLevel: string[] }
> {
  state = {
    previousUrl: '',
    cookieconsentLevel: [],
  };

  componentDidMount() {
    this.state.previousUrl = window.location.pathname;
    Router.events.on('routeChangeComplete', (url) =>
      this.handleRouteChange(this, url)
    );

    if (typeof window !== 'undefined') {
      if (GA_TRACKING_ID) {
        window[`ga-disable-${GA_TRACKING_ID}`] = true;
      }

      if (window.initCookieConsent) {
        this.initCookieConsent();
      } else {
        console.error(
          'Cookie popup failed initialization, as initCookieConsent was not found in window. Attempting again in 1 second...'
        );
        setTimeout(() => {
          if (window.initCookieConsent) {
            this.initCookieConsent();
          } else {
            console.error('Attempt failed again');
          }
        }, 1000);
      }

      setTimeout(() => {
        new Loader({
          apiKey: process.env.NEXT_PUBLIC_GOOGLE_API_KEY,
        })
          .importLibrary('places')
          .then(() => this.forceUpdate());
      }, 100);

      fbq.pageview();

      const vh = document.body.clientHeight * 0.01;
      document.body.style.setProperty('--vh', `${vh}px`);
      const vw = document.body.clientWidth * 0.01;
      document.body.style.setProperty('--vw', `${vw}px`);
      window.addEventListener('resize', this.handleResize);
      setTimeout(() => this.handleResize());
    }
  }

  componentWillUnmount() {
    Router.events.off('routeChangeComplete', this.handleRouteChange);

    window.removeEventListener('resize', this.handleResize);
  }

  initCookieConsent() {
    const cookieConsent = window.initCookieConsent();
    cookieConsent.run({
      ...cookiesData,
      cc_accept_callback: ({ level }) => {
        this.setState({ cookieconsentLevel: level });

        if (GA_TRACKING_ID) {
          window[`ga-disable-${GA_TRACKING_ID}`] = false;
          gtag.pageview(window.location.href);
        }
      },
    });
  }

  handleRouteChange(that: MyApp, url: string) {
    if (that.state.previousUrl.split('?')[0] !== url.split('?')[0]) {
      window.scrollTo(0, 0);
    }
    that.setState({ previousUrl: url });

    gtag.pageview(url);
    gtm.pageview(url);
    fbq.pageview();
  }

  handleResize() {
    const vhResizable = document.body.clientHeight * 0.01;
    document.documentElement.style.setProperty(
      '--vh-resizable',
      `${vhResizable}px`
    );
    const vwResizable = document.body.clientWidth * 0.01;
    document.documentElement.style.setProperty(
      '--vw-resizable',
      `${vwResizable}px`
    );
  }

  render() {
    const { Component, pageProps } = this.props;

    return (
      <ThemeProvider theme={defaultTheme}>
        <I18n>
          <Head>
            <meta charSet="UTF-8" />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1"
            />
            {FB_DOMAIN_VERIFICATION_ID && (
              <meta
                name="facebook-domain-verification"
                content={FB_DOMAIN_VERIFICATION_ID}
              />
            )}
          </Head>

          {this.state.cookieconsentLevel.includes('analytical_cookies') &&
            gtm.GTM_ID && (
              <Script id="gtm-base" strategy="afterInteractive">
                {`
                  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                  })(window,document,'script','dataLayer','${gtm.GTM_ID}');
                `}
              </Script>
            )}
          {this.state.cookieconsentLevel.includes('analytical_cookies') &&
            (fbq.FB_PIXEL_ID || fbq.FB_SANTA_POLA_PIXEL_ID) && (
              <Script id="fb-pixel" strategy="afterInteractive">
                {`
                  !function(f,b,e,v,n,t,s)
                  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                  n.queue=[];t=b.createElement(e);t.async=!0;
                  t.src=v;s=b.getElementsByTagName(e)[0];
                  s.parentNode.insertBefore(t,s)}(window, document,'script',
                  'https://connect.facebook.net/en_US/fbevents.js');
                  ${fbPixelIdInit}
                  ${fbSantaPolaPixelIdInit}
                `}
              </Script>
            )}

          <GlobalStyle />

          <div className={montserrat.className}>
            <Component {...pageProps} />
          </div>
        </I18n>
      </ThemeProvider>
    );
  }
}

export default MyApp as ComponentType;
